/**
 * API base: empty = same origin (Vite proxy in dev).
 * Production: set VITE_API_URL=https://api.yourdomain.com
 */
export function getApiBase(): string {
  const url = import.meta.env.VITE_API_URL;
  return url && url.length > 0 ? url.replace(/\/$/, '') : '';
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  initData: string,
  options: RequestInit = {}
): Promise<T> {
  const base = getApiBase();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
    ...options.headers,
  };
  const res = await fetch(url, { ...options, headers });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const msg =
      typeof data === 'object' && data && 'error' in data
        ? String((data as { error: string }).error)
        : res.statusText;
    throw new ApiError(msg || 'Request failed', res.status, text);
  }
  return data as T;
}

export type StatusResponse = {
  ok: boolean;
  liveTrading: boolean;
  tradingEnabled: boolean;
  balanceUsdt: number | null;
  activeTrades: number;
  retraceTpRetouchClosePct?: number;
  userId?: number;
};

export type TradeTarget = {
  id: string;
  order: number;
  status: string;
  targetPrice: string;
  targetSize: string;
  pullbackAfterReach?: boolean;
  executedAt?: string | null;
  executedPrice?: string | null;
  executedSize?: string | null;
};

export type TradeRow = {
  id: string;
  symbol: string;
  side: string;
  status: string;
  entryPrice: string;
  entrySize: string;
  currentSize: string;
  stopLoss: string | null;
  leverage?: number | null;
  leverageRange?: string | null;
  currentBalance?: string | null;
  openedAt?: string | null;
  isPaper: boolean;
  channelUsername?: string | null;
  targets: TradeTarget[];
};

export type TradesResponse = { ok: boolean; trades: TradeRow[] };
export type HistoryRow = TradeRow & {
  openedAt?: string | null;
  closedAt?: string | null;
  cancelledAt?: string | null;
  bingxPositionId?: string | null;
  /** Sum from executed DB targets (fallback) */
  tpPnlUsdt?: number;
  /** Realized PnL from BingX positionHistory */
  realizedPnlUsdt?: number | null;
  /** Preferred PnL for display (BingX when synced, else TP sum) */
  pnlUsdt?: number;
  pnlSource?: 'bingx' | 'db' | 'estimate';
};
export type HistoryResponse = {
  ok: boolean;
  trades: HistoryRow[];
  bingx?: {
    updatedAt: string | null;
    error: string | null;
    closedPositionCount: number;
    syncedTradeCount: number;
  };
};

export type BingxPositionRow = {
  symbol: string;
  side: string;
  size: number;
  entryPrice: number;
  markPrice?: number | null;
  notional: number | null;
  unrealizedPnl: number | null;
  leverage: number | null;
};

export type PositionsResponse = {
  ok: boolean;
  updatedAt: string | null;
  error: string | null;
  positions: BingxPositionRow[];
};

export const api = {
  status: (initData: string) =>
    request<StatusResponse>('/api/status', initData, { method: 'GET' }),

  trades: (initData: string) =>
    request<TradesResponse>('/api/trades', initData, { method: 'GET' }),

  history: (initData: string) =>
    request<HistoryResponse>('/api/history', initData, { method: 'GET' }),

  positions: (initData: string) =>
    request<PositionsResponse>('/api/positions', initData, { method: 'GET' }),

  closeTrade: (initData: string, id: string) =>
    request<{ ok: boolean; message: string }>(`/api/trades/${id}/close`, initData, {
      method: 'POST',
    }),

  takeTrade: (initData: string, id: string, pct: number) =>
    request<{ ok: boolean; message: string }>(`/api/trades/${id}/take`, initData, {
      method: 'POST',
      body: JSON.stringify({ pct }),
    }),

  deleteTrade: (initData: string, id: string) =>
    request<{ ok: boolean; message: string }>(`/api/trades/${id}`, initData, {
      method: 'DELETE',
    }),

  setTp: (initData: string, id: string) =>
    request<{ ok: boolean; message: string }>(`/api/trades/${id}/set-tp`, initData, {
      method: 'POST',
    }),

  updateTradeTps: (initData: string, id: string, body: { stopLoss: number; takeProfits: number[] }) =>
    request<{ ok: boolean; message: string }>(`/api/trades/${id}/tps`, initData, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  clearHistory: (initData: string) =>
    request<{ ok: boolean; deleted: number; message: string }>('/api/history', initData, {
      method: 'DELETE',
    }),
};
